import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Rating,
  Avatar,
  IconButton,
  keyframes,
} from '@mui/material';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const staggerReveal = (delay: number) => ({
  animation: `${fadeInUp} 0.8s cubic-bezier(0.215, 0.61, 0.355, 1) ${delay}s both`,
});
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { images } from '../assets/images';

// Import components
import BestPractices from '../components/farm/BestPractices';
import ErrorBoundary from '../components/ErrorBoundary';
import { useProducts } from '../context/ProductContext';
import { PageTransition, StaggeredReveal } from '../components/common/PageTransition';

/**
 * Styled hero section with background image and overlay
 * Creates a visually appealing banner at the top of the page
 */
const HeroSection = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${images.heroBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minHeight: '80vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  }
}));

/**
 * Styled card for displaying service offerings
 * Includes hover animation for interactive feel
 */
const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  }
}));

/**
 * Styled card for displaying product information
 * Includes hover animation for interactive feel
 */
const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.03)',
  }
}));

/**
 * Styled media container for product images
 * Ensures consistent image sizing and display
 */
const ProductCardMedia = styled(CardMedia)({
  height: 240,
  objectFit: 'cover',
  objectPosition: 'center',
});

/**
 * Styled content container for product cards
 * Ensures consistent layout of product information
 */
const ProductCardContent = styled(CardContent)({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});

/**
 * Home page component that serves as the landing page
 * Contains sections for hero banner, services, featured products, and testimonials
 * 
 * @returns {JSX.Element} The rendered home page
 */
const Home = () => {
  // Service offerings data
  const services = [
    {
      id: 'farm-price-tracking',
      icon: <DescriptionIcon fontSize="large" />,
      title: 'Farm Produce Price Tracking',
      description: 'Real-time monitoring of agricultural commodity prices'
    },
    {
      id: 'ai-farming-practices',
      icon: <SmartphoneIcon fontSize="large" />,
      title: 'AI-assisted Best Farming Practices',
      description: 'Get personalized recommendations for your farm'
    },
    {
      id: 'produce-management',
      icon: <AssessmentIcon fontSize="large" />,
      title: 'Produce Management and Consulting',
      description: 'Expert guidance on crop management and market trends'
    },
    {
      id: 'import-export-tracking',
      icon: <LocalShippingIcon fontSize="large" />,
      title: 'Government Import/Export Tracking',
      description: 'Stay compliant with agricultural regulations'
    },
    {
      id: 'careers-labor-marketplace',
      icon: <WorkOutlineIcon fontSize="large" />,
      title: 'Careers & Labor Marketplace',
      description: 'Find skilled farm workers or post seasonal and permanent jobs. AI matches you by skills, harvest timing, and location.'
    }
  ];

  const { products: marketplaceProducts } = useProducts();
  const featured = marketplaceProducts.slice(0, 4);

  // Blog posts data
  const blogPosts = [
    {
      id: 'innovation-in-farming',
      title: 'Innovation in Farming',
      image: images.analytics,
      description: 'Discover the latest agricultural technologies'
    },
    {
      id: 'data-analytics',
      title: 'Data Analytics',
      image: images.innovation,
      description: 'Using data to improve farm yields'
    },
    {
      id: 'sustainable-practices',
      title: 'Sustainable Practices',
      image: images.sustainable,
      description: 'Eco-friendly farming methods'
    }
  ];

  // Testimonial data
  const testimonials = [
    {
      id: 1,
      text: "The agricultural dashboard has transformed how we manage our farm data. It's intuitive and provides valuable insights.",
      author: "Madeline Williamson",
      role: "Farm Manager",
      image: images.testimonial1,
      rating: 5
    },
    {
      id: 2,
      text: "Smart farming tools helped me increase my crop yield by 30% this season. The pest detection feature saved my entire harvest!",
      author: "John Kimani",
      role: "Small-scale Farmer",
      image: images.testimonial1, // Reusing image - should be replaced with actual images
      rating: 5
    },
    {
      id: 3,
      text: "The marketplace feature connected me directly with buyers, eliminating middlemen and increasing my profits substantially.",
      author: "Sarah Muthoni",
      role: "Commercial Farmer",
      image: images.testimonial1, // Reusing image - should be replaced with actual images
      rating: 4
    }
  ];

  // Add this state for testimonials
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = React.useState(0);

  const handleNextTestimonial = () => {
    setCurrentTestimonialIndex((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const handlePrevTestimonial = () => {
    setCurrentTestimonialIndex((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  return (
    <PageTransition>
      {/* Hero Section - Main banner with call-to-action */}
      <HeroSection>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <StaggeredReveal delay={0.1}>
            <Typography
              variant="h2"
              color="white"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              The Role of Technology
              <br />
              in Revolutionizing
              <br />
              Agriculture
            </Typography>
          </StaggeredReveal>
          <StaggeredReveal delay={0.2}>
            <Typography variant="h6" color="white" paragraph sx={{ mb: 4 }}>
              Empowering farmers with modern solutions for sustainable agriculture
            </Typography>
          </StaggeredReveal>
          <StaggeredReveal delay={0.3}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                component={Link}
                to="/dashboard"
              >
                Get Started
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                sx={{ color: 'white', borderColor: 'white' }}
                component={Link}
                to="/about"
              >
                Learn More
              </Button>
            </Box>
          </StaggeredReveal>
        </Container>
      </HeroSection>

      {/* Passion Section - Company mission and values */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <StaggeredReveal delay={0.4}>
              <Typography variant="overline" color="primary" fontWeight="bold">
                OUR MISSION
              </Typography>
              <Typography variant="h3" gutterBottom color="text.primary" sx={{ fontWeight: 'bold', mb: 3 }}>
                Nurturing Growth &<br />Sustaining the Future
              </Typography>
              <Typography variant="body1" paragraph color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                We believe in a future where technology bridges the gap between traditional farming and modern efficiency.
                By providing tools for precision agriculture, market access, and data analytics, we are building a
                resilient food system for Kenya and beyond.
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoneyIcon color="success" sx={{ mr: 2 }} />
                  <Typography variant="body1">Increase farm profitability by up to 40%</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WbSunnyIcon color="warning" sx={{ mr: 2 }} />
                  <Typography variant="body1">Climate-resilient farming practices</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssessmentIcon color="primary" sx={{ mr: 2 }} />
                  <Typography variant="body1">Data-driven decision making</Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                color="primary"
                endIcon={<ArrowForwardIcon />}
                component={Link}
                to="/about"
                sx={{ mt: 4 }}
              >
                Read Our Story
              </Button>
            </StaggeredReveal>
          </Grid>
          <Grid item xs={12} md={6}>
            <StaggeredReveal delay={0.5}>
              <Box
                component="img"
                src={images.farmField}
                alt="Farm field"
                sx={{
                  width: '100%',
                  height: 400,
                  objectFit: 'cover',
                  borderRadius: 4,
                  boxShadow: 4,
                }}
              />
            </StaggeredReveal>
          </Grid>
        </Grid>
      </Container>

      {/* Services Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <StaggeredReveal delay={0.6}>
              <Typography variant="overline" color="primary" fontWeight="bold">
                OUR SERVICES
              </Typography>
              <Typography variant="h3" gutterBottom fontWeight="bold">
                Comprehensive Agricultural Solutions
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                Everything you need to succeed in modern agriculture, from soil to market.
              </Typography>
            </StaggeredReveal>
          </Box>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <StaggeredReveal delay={0.7 + index * 0.1}>
                  <ServiceCard>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {service.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom align="center">
                      {service.title}
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary" paragraph>
                      {service.description}
                    </Typography>
                    {service.id === 'careers-labor-marketplace' ? (
                      <Button
                        component={Link}
                        to="/dashboard/careers"
                        variant="contained"
                        color="success"
                        endIcon={<ArrowForwardIcon />}
                      >
                        Find Work or Hire
                      </Button>
                    ) : (
                      <Button
                        component={Link}
                        to={`/services/${service.id}`}
                        endIcon={<ArrowForwardIcon />}
                      >
                        Read More
                      </Button>
                    )}
                  </ServiceCard>
                </StaggeredReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Featured Products */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <StaggeredReveal delay={1.1}>
          <Typography variant="h4" align="center" gutterBottom>
            Our Featured Products
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" paragraph>
            High-quality agricultural products for optimal yields
          </Typography>
        </StaggeredReveal>
        <Grid container spacing={4}>
          {(featured.length > 0 ? featured : []).map((product: any, index: number) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StaggeredReveal delay={1.2 + index * 0.1}>
                <ProductCard>
                  <Box
                    component="img"
                    src={product.image}
                    alt={product.name}
                    sx={{
                      height: 240,
                      width: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                  />
                  <ProductCardContent>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {product.description}
                      </Typography>
                    </Box>
                    <Button
                      variant="text"
                      color="primary"
                      component={Link}
                      to={`/marketplace/product/${product.id}`}
                      sx={{ mt: 2, alignSelf: 'flex-start' }}
                      endIcon={<ArrowForwardIcon />}
                    >
                      Learn More
                    </Button>
                  </ProductCardContent>
                </ProductCard>
              </StaggeredReveal>
            </Grid>
          ))}
        </Grid>
      </Container>
    </PageTransition>
  );
};

export default Home;
